import React, { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  X, Crown, Copy, Check, Printer, FileText, AlertTriangle, Loader2,
  DollarSign, Building2, Swords, Calendar,
} from 'lucide-react';
import { loggedGet } from './loggedFetch';
import { ReportSections, reportToMarkdown } from './DisplacementReportModal';
import StepChecklist from './StepChecklist';
import logger from '../../lib/logger';
import './LeadershipRollup.css';

/*
 * LeadershipRollup — single-page presentation view aggregating the
 * vendor's snapshot KPIs + every card in the 30-60-90 plan with each
 * card's full Master-Architect-grade Opus briefing rendered inline.
 *
 * Designed to be the document a super-admin actually walks into a
 * leadership / CIO meeting holding. Three top-level actions:
 *   - Copy all as markdown (one paste → email / Notion / Doc)
 *   - Print (browser-native PDF; reuses the same body.drm-printing
 *     stylesheet pattern the per-card modal uses)
 *   - Close (back to the dashboard)
 *
 * Pulls plan + reports in one round trip via ?include=reports so
 * opening the rollup doesn't fan out N+1 fetches.
 */

const COLUMNS = [
  { key: 'backlog', label: 'Backlog' },
  { key: '30', label: '30 days' },
  { key: '60', label: '60 days' },
  { key: '90', label: '90 days' },
];

function formatCurrency(num) {
  const n = Number(num) || 0;
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function formatDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function LeadershipRollup({ isOpen, vendor, onClose, includeLowConfidence, strict }) {
  const [copyState, setCopyState] = useState(false);

  const snapshotQs = new URLSearchParams();
  if (includeLowConfidence) snapshotQs.set('includeLowConfidence', 'true');
  if (!strict) snapshotQs.set('strict', 'false');
  const snapSuffix = snapshotQs.toString() ? `?${snapshotQs}` : '';

  const snapshotQuery = useQuery({
    queryKey: ['vendor-analysis', 'snapshot', vendor, includeLowConfidence, strict],
    queryFn: loggedGet(
      'LeadershipRollup:snapshot',
      `/api/vendor-analysis/${encodeURIComponent(vendor || '')}/snapshot${snapSuffix}`
    ),
    enabled: !!vendor && isOpen,
  });

  const planQuery = useQuery({
    queryKey: ['vendor-analysis', 'plan-with-reports', vendor],
    queryFn: loggedGet(
      'LeadershipRollup:plan',
      `/api/vendor-analysis/${encodeURIComponent(vendor || '')}/plan?include=reports`
    ),
    enabled: !!vendor && isOpen,
  });

  // Pull a smaller-scope partner ranking for the rollup so leadership
  // can see "who's already pushing this" at a glance. Same key as
  // PartnerEcosystem on the main page so React Query dedupes.
  const partnersQuery = useQuery({
    queryKey: ['vendor-analysis', 'partners', vendor, includeLowConfidence, strict],
    queryFn: loggedGet(
      'LeadershipRollup:partners',
      `/api/vendor-analysis/${encodeURIComponent(vendor || '')}/partners?limit=10${
        includeLowConfidence ? '&includeLowConfidence=true' : ''
      }${!strict ? '&strict=false' : ''}`
    ),
    enabled: !!vendor && isOpen,
  });

  const cardsByColumn = useMemo(() => {
    const grouped = { backlog: [], 30: [], 60: [], 90: [] };
    for (const c of (planQuery.data?.cards || [])) {
      if (grouped[c.column_key]) grouped[c.column_key].push(c);
    }
    for (const k of Object.keys(grouped)) grouped[k].sort((a, b) => a.position - b.position);
    return grouped;
  }, [planQuery.data]);

  const orderedCards = useMemo(() => {
    return COLUMNS.flatMap(col =>
      cardsByColumn[col.key].map(c => ({ ...c, column_label: col.label }))
    );
  }, [cardsByColumn]);

  const totals = useMemo(() => {
    const cards = planQuery.data?.cards || [];
    const total = cards.reduce((sum, c) => sum + (Number(c.dollar_impact) || 0), 0);
    const ready = cards.filter(c => c.report_status === 'ready').length;
    const generating = cards.filter(c => c.report_status === 'generating').length;
    const agencies = new Set(cards.map(c => c.seed_payload?.agency).filter(Boolean));
    const competitors = new Set(cards.map(c => c.seed_payload?.competitor).filter(Boolean));
    return {
      card_count: cards.length,
      total_at_risk: total,
      reports_ready: ready,
      reports_generating: generating,
      agency_count: agencies.size,
      competitor_count: competitors.size,
    };
  }, [planQuery.data]);

  const handleCopyAll = useCallback(async () => {
    const cards = planQuery.data?.cards || [];
    const lines = [];
    lines.push(`# Vendor Leadership Rollup — ${vendor}`);
    lines.push(`_Generated ${new Date().toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}_`);
    lines.push('');

    if (snapshotQuery.data) {
      const snap = snapshotQuery.data;
      lines.push('## State-wide footprint');
      lines.push(`- **Total spend:** ${formatCurrency(snap.total_spend)} across ${(snap.po_line_count || 0).toLocaleString()} line items`);
      lines.push(`- **Agencies with active spend:** ${snap.agency_count?.toLocaleString() || '—'}`);
      lines.push(`- **Competitors sharing categories:** ${snap.competitor_count?.toLocaleString() || '—'}`);
      lines.push('');
    }

    const partners = partnersQuery.data?.partners || [];
    if (partners.length > 0) {
      lines.push(`## Partner ecosystem (top ${partners.length} by spend)`);
      lines.push('| # | Partner | Spend | Agencies | POs | Top categories |');
      lines.push('|---|---|---|---|---|---|');
      partners.forEach((p, i) => {
        const cats = (p.top_categories || []).slice(0, 3).join(' · ') || '—';
        lines.push(`| ${i + 1} | ${p.supplier_name} | ${formatCurrency(p.total_spend)} | ${p.agency_count} | ${p.po_count} | ${cats} |`);
      });
      lines.push('');
    }

    lines.push('## Plan summary');
    lines.push(`- ${totals.card_count} plays in the 30-60-90 plan`);
    lines.push(`- ${formatCurrency(totals.total_at_risk)} of dollar impact in flight`);
    lines.push(`- Across ${totals.agency_count} agencies and ${totals.competitor_count} competitive incumbents`);
    lines.push(`- ${totals.reports_ready} of ${totals.card_count} CIO briefings ready`);
    lines.push('');

    for (const col of COLUMNS) {
      const cards = cardsByColumn[col.key];
      if (cards.length === 0) continue;
      lines.push(`## ${col.label}`);
      cards.forEach((card, i) => {
        lines.push(`### ${i + 1}. ${card.title}`);
        const meta = [];
        if (card.seed_payload?.agency) meta.push(card.seed_payload.agency);
        if (card.seed_payload?.competitor) meta.push(`vs ${card.seed_payload.competitor}`);
        if (card.dollar_impact != null) meta.push(formatCurrency(card.dollar_impact));
        if (card.due_date) meta.push(`due ${formatDate(card.due_date)}`);
        if (meta.length) lines.push(`_${meta.join(' · ')}_`);
        if (card.rationale) lines.push(`\n${card.rationale}`);
        if (card.report) {
          lines.push('');
          lines.push(reportToMarkdown({
            vendor,
            card,
            report: card.report,
            generatedAt: card.report_generated_at,
            model: card.report_model,
          }));
        } else if (card.report_status === 'generating') {
          lines.push('');
          lines.push('_(Executive briefing generating — refresh and re-export to include it.)_');
        } else if (card.report_status === 'failed') {
          lines.push('');
          lines.push(`_(Briefing generation failed: ${card.report_error || 'unknown error'})_`);
        } else {
          lines.push('');
          lines.push('_(No executive briefing yet for this card.)_');
        }

        // Action checklist with current status — includes whatever
        // the user has checked off + any manual steps they added.
        if ((card.steps || []).length > 0) {
          lines.push('');
          lines.push(`**Action checklist (${card.steps_done || 0}/${card.steps_total || card.steps.length} done)**`);
          for (const step of card.steps) {
            const box = step.status === 'done' ? '[x]' : '[ ]';
            const owner = step.owner ? ` _(${step.owner})_` : '';
            lines.push(`- ${box} ${step.step_text}${owner}`);
            if (step.outcome) lines.push(`  - ${step.outcome}`);
          }
        }

        lines.push('');
      });
    }

    lines.push('---');
    lines.push('_Verify all claims with the customer before committing in writing — LLMs can confidently misstate facts._');

    try {
      await navigator.clipboard.writeText(lines.join('\n'));
      setCopyState(true);
      setTimeout(() => setCopyState(false), 1800);
      logger.vendorAnalysis.info('LeadershipRollup:copy-all', {
        vendor, card_count: cards.length, char_count: lines.join('\n').length,
      });
    } catch (err) {
      logger.vendorAnalysis.error('LeadershipRollup:copy-all:failed', { vendor }, err);
    }
  }, [planQuery.data, snapshotQuery.data, partnersQuery.data, cardsByColumn, totals, vendor]);

  const handlePrint = useCallback(() => {
    document.body.classList.add('drm-printing');
    document.body.classList.add('lr-printing');
    const cleanup = () => {
      document.body.classList.remove('drm-printing');
      document.body.classList.remove('lr-printing');
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    logger.vendorAnalysis.info('LeadershipRollup:print', {
      vendor, card_count: planQuery.data?.cards?.length || 0,
    });
    window.print();
  }, [vendor, planQuery.data]);

  if (!isOpen) return null;

  const isLoading = snapshotQuery.isLoading || planQuery.isLoading;
  const error = snapshotQuery.error || planQuery.error;

  return (
    <div className="lr-overlay">
      <div className="lr-frame">
        <header className="lr-frame-head">
          <div className="lr-eyebrow">
            <Crown size={14} />
            <span>Leadership Roll-up</span>
          </div>
          <div className="lr-actions">
            <button
              className="drm-icon-btn"
              onClick={handleCopyAll}
              disabled={isLoading || !!error}
              title="Copy snapshot + every plan card with its CIO briefing as one big markdown blob"
            >
              {copyState ? <Check size={14} /> : <Copy size={14} />}
              <span>{copyState ? 'Copied' : 'Copy all as markdown'}</span>
            </button>
            <button
              className="drm-icon-btn"
              onClick={handlePrint}
              disabled={isLoading || !!error}
              title="Browser-native PDF export of the entire roll-up"
            >
              <Printer size={14} />
              <span>Print / PDF</span>
            </button>
            <button className="drm-close" onClick={onClose} aria-label="Close">
              <X size={18} />
            </button>
          </div>
        </header>

        <div className="lr-body">
          {isLoading && (
            <div className="drm-loading">
              <Loader2 size={20} className="drm-spin" />
              <div>
                <strong>Assembling roll-up…</strong>
                <p>Pulling state-wide footprint and every plan card's executive briefing.</p>
              </div>
            </div>
          )}
          {error && (
            <div className="drm-error">
              <AlertTriangle size={18} />
              <div>
                <strong>Couldn't assemble the roll-up: {error.message}</strong>
              </div>
            </div>
          )}

          {!isLoading && !error && (
            <>
              <RollupTitle vendor={vendor} totals={totals} />
              {snapshotQuery.data && <SnapshotKpis snapshot={snapshotQuery.data} />}
              {partnersQuery.data?.partners?.length > 0 && (
                <PartnersSection partners={partnersQuery.data.partners} summary={partnersQuery.data.summary} />
              )}
              <PlanSummary totals={totals} />

              {orderedCards.length === 0 ? (
                <div className="lr-empty">
                  <FileText size={20} />
                  <div>
                    <strong>No plays in the plan yet.</strong>
                    <p>Add cards from the displacement table or renewal calendar.</p>
                  </div>
                </div>
              ) : (
                <div className="lr-card-stack">
                  {COLUMNS.map(col => {
                    const colCards = cardsByColumn[col.key];
                    if (colCards.length === 0) return null;
                    return (
                      <section key={col.key} className="lr-column">
                        <h2 className="lr-column-title">{col.label}</h2>
                        {colCards.map((card, idx) => (
                          <RollupCard
                            key={card.id}
                            card={card}
                            vendor={vendor}
                            index={idx + 1}
                            columnLabel={col.label}
                          />
                        ))}
                      </section>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        <footer className="lr-foot">
          Verify all claims with the customer before committing in writing — LLMs can confidently misstate facts.
        </footer>
      </div>
    </div>
  );
}

function RollupTitle({ vendor, totals }) {
  return (
    <div className="lr-title-block">
      <h1>{vendor} — Leadership Roll-up</h1>
      <p className="lr-subtitle">
        State-wide California procurement footprint + the active 30-60-90
        plan, with each play's Master-Architect-level CIO briefing.
        {' '}<span className="lr-meta">Generated {new Date().toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' })}</span>
      </p>
      <div className="lr-headline-meta">
        {totals.card_count > 0 && (
          <span><strong>{totals.card_count}</strong> plays</span>
        )}
        {totals.total_at_risk > 0 && (
          <span><strong>{formatCurrency(totals.total_at_risk)}</strong> in flight</span>
        )}
        {totals.agency_count > 0 && (
          <span><strong>{totals.agency_count}</strong> agencies</span>
        )}
        {totals.competitor_count > 0 && (
          <span><strong>{totals.competitor_count}</strong> incumbent vendors</span>
        )}
      </div>
    </div>
  );
}

function SnapshotKpis({ snapshot }) {
  const dateRange = snapshot.first_po_date && snapshot.last_po_date
    ? `${new Date(snapshot.first_po_date).getFullYear()}–${new Date(snapshot.last_po_date).getFullYear()}`
    : '—';
  return (
    <section className="lr-section">
      <h2 className="lr-section-title">State-wide footprint</h2>
      <div className="lr-kpi-grid">
        <div className="lr-kpi">
          <div className="lr-kpi-label"><DollarSign size={12} /> Total spend</div>
          <div className="lr-kpi-value">{formatCurrency(snapshot.total_spend)}</div>
          <div className="lr-kpi-sub">{(snapshot.po_line_count || 0).toLocaleString()} line items · {dateRange}</div>
        </div>
        <div className="lr-kpi">
          <div className="lr-kpi-label"><Building2 size={12} /> Agencies</div>
          <div className="lr-kpi-value">{(snapshot.agency_count || 0).toLocaleString()}</div>
          <div className="lr-kpi-sub">CA agencies with active spend</div>
        </div>
        <div className="lr-kpi">
          <div className="lr-kpi-label"><Swords size={12} /> Competitors</div>
          <div className="lr-kpi-value">{(snapshot.competitor_count || 0).toLocaleString()}</div>
          <div className="lr-kpi-sub">Vendors sharing at least one category</div>
        </div>
      </div>
    </section>
  );
}

function PartnersSection({ partners, summary }) {
  const totalSpend = summary?.total_spend || partners.reduce((s, p) => s + (p.total_spend || 0), 0);
  return (
    <section className="lr-section">
      <h2 className="lr-section-title">
        Partner ecosystem · who's pushing this tech
      </h2>
      <p className="lr-subsection-sub">
        Top {partners.length} resellers ranked by CA state spend, totalling
        {' '}<strong>{formatCurrency(totalSpend)}</strong> of vendor tech.
        These are the channel introductions to leverage for expansion.
      </p>
      <div className="lr-partners-table-wrap">
        <table className="lr-partners-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Partner</th>
              <th className="lr-num">Spend</th>
              <th className="lr-num">Agencies</th>
              <th className="lr-num">POs</th>
              <th>Top categories</th>
            </tr>
          </thead>
          <tbody>
            {partners.map((p, i) => (
              <tr key={p.supplier_name}>
                <td>{i + 1}</td>
                <td className="lr-partner-name">{p.supplier_name}</td>
                <td className="lr-num"><strong>{formatCurrency(p.total_spend)}</strong></td>
                <td className="lr-num">{p.agency_count}</td>
                <td className="lr-num">{p.po_count}</td>
                <td>
                  <div className="lr-partner-chips">
                    {(p.top_categories || []).slice(0, 3).map((c, idx) => (
                      <span key={idx} className="lr-partner-chip">{c}</span>
                    ))}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PlanSummary({ totals }) {
  return (
    <section className="lr-section">
      <h2 className="lr-section-title">Plan summary</h2>
      <div className="lr-kpi-grid">
        <div className="lr-kpi">
          <div className="lr-kpi-label"><FileText size={12} /> Plays in plan</div>
          <div className="lr-kpi-value">{totals.card_count}</div>
          <div className="lr-kpi-sub">across backlog, 30, 60, 90 days</div>
        </div>
        <div className="lr-kpi">
          <div className="lr-kpi-label"><DollarSign size={12} /> Dollars in flight</div>
          <div className="lr-kpi-value">{formatCurrency(totals.total_at_risk)}</div>
          <div className="lr-kpi-sub">sum of card dollar impacts</div>
        </div>
        <div className="lr-kpi">
          <div className="lr-kpi-label"><Calendar size={12} /> Briefings ready</div>
          <div className="lr-kpi-value">
            {totals.reports_ready}<span className="lr-kpi-frac"> / {totals.card_count}</span>
          </div>
          <div className="lr-kpi-sub">
            {totals.reports_generating > 0 ? `${totals.reports_generating} generating · ` : ''}
            Opus 4.7 CIO briefings
          </div>
        </div>
      </div>
    </section>
  );
}

function RollupCard({ card, vendor, index, columnLabel }) {
  const seed = card.seed_payload || {};
  return (
    <article className="lr-card">
      <header className="lr-card-head">
        <div className="lr-card-eyebrow">
          <span className="lr-card-column">{columnLabel}</span>
          <span className="lr-card-index">Play #{index}</span>
        </div>
        <h3 className="lr-card-title">{card.title}</h3>
        <div className="lr-card-meta">
          <span>{vendor}</span>
          {seed.competitor && <span>vs {seed.competitor}</span>}
          {seed.agency && <span>{seed.agency}</span>}
          {card.dollar_impact != null && <span>{formatCurrency(card.dollar_impact)}</span>}
          {card.due_date && <span>due {formatDate(card.due_date)}</span>}
        </div>
        {card.rationale && (
          <p className="lr-card-rationale">{card.rationale}</p>
        )}
      </header>

      {card.report ? (
        <ReportSections
          report={card.report}
          model={card.report_model}
          generatedAt={card.report_generated_at}
        />
      ) : (
        <div className="lr-card-no-report">
          {card.report_status === 'generating' ? (
            <><Loader2 size={14} className="drm-spin" /> Briefing generating — refresh shortly.</>
          ) : card.report_status === 'failed' ? (
            <><AlertTriangle size={14} /> Briefing failed: {card.report_error || 'unknown error'}.</>
          ) : (
            <><FileText size={14} /> No executive briefing yet for this card.</>
          )}
        </div>
      )}

      {(card.steps_total > 0 || (card.steps || []).length > 0) && (
        <div className="lr-card-steps">
          <h4 className="lr-card-steps__head">Action checklist</h4>
          <StepChecklist
            vendor={vendor}
            cardId={card.id}
            steps={card.steps || []}
            stepsDone={card.steps_done || 0}
            stepsTotal={card.steps_total || 0}
            compact={false}
          />
        </div>
      )}
    </article>
  );
}

export default LeadershipRollup;
